import { fabric } from 'fabric';
import { METRIC_KEY } from '../../utilities/constants/Constants';
import { convertImperialToGivenUnitSystem } from '../../utilities/HelperFunctions/HelperFunctions';

export const addMeasurementLines = (
  canvasObject,
  canvas,
  wallSize,
  unitSystem
) => {
  if (
    !(
      Object.prototype.hasOwnProperty.call(canvasObject, 'measurementLines') &&
      canvasObject.measurementLines
    )
  ) {
    const { projectHeight, projectWidth } = wallSize;
    const measurementLines = [];
    const canvasObjectCoords = canvasObject.aCoords;
    const canvasObjectCenterPoint = canvasObject.getCenterPoint();
    const scale = projectHeight / canvas.height;

    const leftLineDict = drawArrowWithLabel(
      getLeftLineCoordinates(canvasObjectCenterPoint, canvasObjectCoords),
      getDistanceLabel(
        getLeftDistance(canvasObject, scale, unitSystem),
        unitSystem
      ),
      canvas,
      'left'
    );
    measurementLines.push(leftLineDict);

    const rightLineDict = drawArrowWithLabel(
      getRightLineCoordinates(
        canvasObjectCenterPoint,
        canvasObjectCoords,
        canvas.width
      ),
      getDistanceLabel(
        getRightDistance(canvasObject, scale, projectWidth, unitSystem),
        unitSystem
      ),
      canvas,
      'right'
    );
    measurementLines.push(rightLineDict);

    const toptLineDict = drawArrowWithLabel(
      getTopLineCoordinates(canvasObjectCenterPoint, canvasObjectCoords),
      getDistanceLabel(
        getTopDistance(canvasObject, scale, unitSystem),
        unitSystem
      ),
      canvas,
      'top'
    );
    measurementLines.push(toptLineDict);

    const bottomLineDict = drawArrowWithLabel(
      getBottomLineCoordinates(
        canvasObjectCenterPoint,
        canvasObjectCoords,
        canvas.height
      ),
      getDistanceLabel(
        getBottomDistance(canvasObject, scale, projectHeight, unitSystem),
        unitSystem
      ),
      canvas,
      'bottom'
    );
    measurementLines.push(bottomLineDict);
    canvasObject.measurementLines = measurementLines;

    canvas.renderAll();
  }
};

export const refreshMeasurementLines = (
  canvasObject,
  canvas,
  wallSize,
  unitSystem
) => {
  const { projectWidth, projectHeight } = wallSize;
  const canvasObjectCoords = canvasObject.aCoords;
  const canvasObjectCenterPoint = canvasObject.getCenterPoint();
  const scale = projectHeight / canvas.height;

  updateArrowPosition(
    canvasObject.measurementLines[0],
    getLeftLineCoordinates(canvasObjectCenterPoint, canvasObjectCoords),
    getDistanceLabel(
      getLeftDistance(canvasObject, scale, unitSystem),
      unitSystem
    ),
    'left'
  );
  updateArrowPosition(
    canvasObject.measurementLines[1],
    getRightLineCoordinates(
      canvasObjectCenterPoint,
      canvasObjectCoords,
      canvas.width
    ),
    getDistanceLabel(
      getRightDistance(canvasObject, scale, projectWidth, unitSystem),
      unitSystem
    ),
    'right'
  );
  updateArrowPosition(
    canvasObject.measurementLines[2],
    getTopLineCoordinates(canvasObjectCenterPoint, canvasObjectCoords),
    getDistanceLabel(
      getTopDistance(canvasObject, scale, unitSystem),
      unitSystem
    ),
    'top'
  );
  updateArrowPosition(
    canvasObject.measurementLines[3],
    getBottomLineCoordinates(
      canvasObjectCenterPoint,
      canvasObjectCoords,
      canvas.height
    ),
    getDistanceLabel(
      getBottomDistance(canvasObject, scale, projectHeight, unitSystem),
      unitSystem
    ),
    'bottom'
  );
  canvas.renderAll();
};

export const drawArrowWithLabel = (coords, label, canvas, position) => {
  const line = new fabric.Line(coords, {
    fill: 'black',
    stroke: 'black',
    strokeWidth: 1,
    selectable: false,
    evented: false
  });
  canvas.add(line);

  let leftAngle = 0;
  let rightAngle = 180;
  if (position === 'left' || position === 'right') {
    leftAngle = 270;
    rightAngle = 90;
  }
  const triangleLeft = new fabric.Triangle({
    left: coords[0],
    top: coords[1],
    strokeWidth: 1,
    width: 7,
    height: 7,
    stroke: 'black',
    angle: leftAngle,
    fill: 'black',
    originX: 'center'
  });
  canvas.add(triangleLeft);

  const triangleRight = new fabric.Triangle({
    left: coords[2],
    top: coords[3],
    strokeWidth: 1,
    width: 7,
    height: 7,
    stroke: 'black',
    angle: rightAngle,
    fill: 'black',
    originX: 'center'
  });
  canvas.add(triangleRight);

  let lineCeterPointX = 0;
  let lineCeterPointY = 0;
  switch (position) {
    case 'left':
    case 'right':
      lineCeterPointX = coords[0] + (coords[2] - coords[0]) / 2;
      lineCeterPointY = coords[1] - 8;
      break;
    case 'top':
    case 'bottom':
      lineCeterPointX = coords[0] - 16;
      lineCeterPointY = coords[1] + (coords[3] - coords[1]) / 2;
      break;

    default:
      break;
  }

  const distanceLabel = `${parseFloat(label).toFixed(2)}"`;
  const textLabel = new fabric.Text(distanceLabel, {
    left: lineCeterPointX,
    top: lineCeterPointY,
    fontFamily: 'HPSimplified',
    fontSize: 12,
    textBackgroundColor: 'black'
  });
  textLabel.setColor('#fff');
  canvas.add(textLabel);

  const controlDict = {
    line,
    label: textLabel,
    leftArrow: triangleLeft,
    rightArrow: triangleRight
  };
  return controlDict;
};

export const getLeftLineCoordinates = (
  canvasObjectCenterPoint,
  canvasObjectCoords
) => {
  const canvasObjectWidth = canvasObjectCoords.br.x - canvasObjectCoords.bl.x;
  return [
    0,
    canvasObjectCenterPoint.y,
    canvasObjectCenterPoint.x - canvasObjectWidth / 2,
    canvasObjectCenterPoint.y
  ];
};

export const getRightLineCoordinates = (
  canvasObjectCenterPoint,
  canvasObjectCoords,
  width
) => {
  const canvasObjectWidth = canvasObjectCoords.br.x - canvasObjectCoords.bl.x;
  return [
    canvasObjectCenterPoint.x + canvasObjectWidth / 2,
    canvasObjectCenterPoint.y,
    width,
    canvasObjectCenterPoint.y
  ];
};

export const getTopLineCoordinates = (
  canvasObjectCenterPoint,
  canvasObjectCoords
) => {
  const canvasObjectHeight = canvasObjectCoords.br.y - canvasObjectCoords.tr.y;
  return [
    canvasObjectCenterPoint.x,
    0,
    canvasObjectCenterPoint.x,
    canvasObjectCenterPoint.y - canvasObjectHeight / 2
  ];
};

export const getBottomLineCoordinates = (
  canvasObjectCenterPoint,
  canvasObjectCoords,
  height
) => {
  const canvasObjectHeight = canvasObjectCoords.br.y - canvasObjectCoords.tr.y;
  return [
    canvasObjectCenterPoint.x,
    canvasObjectCenterPoint.y + canvasObjectHeight / 2,
    canvasObjectCenterPoint.x,
    height
  ];
};

export const getLeftDistance = (canvasObject, scale, unitSystem) =>
  convertImperialToGivenUnitSystem(canvasObject.left * scale, unitSystem);

export const getRightDistance = (canvasObject, scale, wallWidth, unitSystem) =>
  convertImperialToGivenUnitSystem(
    wallWidth -
      (canvasObject.left + canvasObject.width * canvasObject.scaleX) * scale,
    unitSystem
  );

export const getTopDistance = (canvasObject, scale, unitSystem) =>
  convertImperialToGivenUnitSystem(canvasObject.top * scale, unitSystem);

export const getBottomDistance = (
  canvasObject,
  scale,
  wallHeight,
  unitSystem
) =>
  convertImperialToGivenUnitSystem(
    wallHeight -
      (canvasObject.top + canvasObject.height * canvasObject.scaleY) * scale,
    unitSystem
  );

export const getDistanceLabel = (distance, unitSystem) => {
  let unitSymbol = '"';
  if (unitSystem == METRIC_KEY) {
    unitSymbol = 'cm';
  }
  return `${parseFloat(distance).toFixed(2)}${unitSymbol}`;
};

export const updateArrowPosition = (arrow, coords, distanceLabel, position) => {
  if (coords.length === 4) {
    arrow.line.set({
      x1: coords[0],
      y1: coords[1],
      x2: coords[2],
      y2: coords[3]
    });

    arrow.leftArrow.set({ left: coords[0], top: coords[1] });
    arrow.rightArrow.set({ left: coords[2], top: coords[3] });

    let lineCeterPointX = 0;
    let lineCeterPointY = 0;
    switch (position) {
      case 'left':
      case 'right':
        lineCeterPointX = coords[0] + (coords[2] - coords[0]) / 2;
        lineCeterPointY = coords[1] - 8;
        break;
      case 'top':
        lineCeterPointX = coords[0] - 16;
        lineCeterPointY = coords[1] + (coords[3] - coords[1]) / 2;
        break;
      case 'bottom':
        lineCeterPointX = coords[0] - 16;
        lineCeterPointY =
          coords[1] + (coords[3] - coords[1]) / 2 - arrow.label.height;
        break;
      default:
        break;
    }
    arrow.label.text = distanceLabel;
    arrow.label.set({
      left: lineCeterPointX,
      top: lineCeterPointY
    });
  }
};

export const removeMeasurementLines = (canvasObject, canvas) => {
  if (canvasObject.measurementLines) {
    canvasObject.measurementLines.forEach(lineDict => {
      canvas.remove(lineDict.line);
      canvas.remove(lineDict.label);
      canvas.remove(lineDict.leftArrow);
      canvas.remove(lineDict.rightArrow);
    });
    canvasObject.measurementLines = null;
  }
};
